import { sanitizeApiKey } from 'js/lib/URLUtils';

interface EndpointParam {
  key: string;
  value: string;
  required?: boolean;
}

interface EndpointSpec {
  method: string;
  route: string;
  headers?: EndpointParam[];
  path_params?: EndpointParam[];
  query_params?: EndpointParam[];
  body?: any;
}

/**
 * Filter parameters to only include those with valid keys
 * @param params - Array of endpoint parameters
 * @returns Filtered array of parameters with keys
 */
export function filterParametersWithKeys(params: any[]): EndpointParam[] {
  if (!Array.isArray(params)) return [];
  return params.filter((param) => param && param.key);
}

/**
 * Generate a cURL command example for an API endpoint
 * @param endpointSpec - The endpoint specification with method, route, params, etc.
 * @param url - The base URL for the API server
 * @param apiKey - The API key for authentication
 * @param sanitizeKey - Whether to sanitize the API key in the output (default: true)
 * @returns A formatted cURL command string
 */
export function generateCurlExample(
  endpointSpec: EndpointSpec | null | undefined,
  url: string,
  apiKey: string,
  sanitizeKey: boolean = true,
): string {
  if (!endpointSpec) return '';

  const formattedApiKey = sanitizeKey ? sanitizeApiKey(apiKey) : apiKey;
  const { method, route, body } = endpointSpec;

  const validQueryParams = filterParametersWithKeys(endpointSpec.query_params || []);
  const validPathParams = filterParametersWithKeys(endpointSpec.path_params || []);
  const validHeaders = filterParametersWithKeys(endpointSpec.headers || []);

  let requestEndpoint = `${url}${route}`;

  // Add query parameters (only if they have keys)
  const urlSearchParams = new URLSearchParams();
  validQueryParams.forEach((param) => {
    urlSearchParams.append(param.key, param.value || 'value');
  });
  urlSearchParams.append('api_key', formattedApiKey);
  const queryString = urlSearchParams.toString();
  if (queryString) {
    requestEndpoint += `?${queryString}`;
  }

  // Add path parameters (only if they have keys)
  if (validPathParams.length > 0) {
    validPathParams.forEach((param) => {
      requestEndpoint = requestEndpoint.replaceAll(`{${param.key}}`, param.value || '');
    });
  }

  let curl = `curl -X ${method} "${requestEndpoint}"`;

  // Add headers (only if they have keys)
  if (validHeaders.length > 0) {
    validHeaders.forEach((header) => {
      curl += ` \\\n  -H "${header.key}: ${header.value || 'value'}"`;
    });
  }

  // Add body for POST/PUT requests (only if body present)
  if (['POST', 'PUT'].includes(method) && body) {
    curl += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`;
  }

  return curl;
}

export function hasEndpointConfiguration(endpointSpec: EndpointSpec | undefined): boolean {
  if (!endpointSpec) return false;

  const hasHeaders = endpointSpec.headers && endpointSpec.headers.length > 0;
  const hasPathParams = endpointSpec.path_params && endpointSpec.path_params.length > 0;
  const hasQueryParams = endpointSpec.query_params && endpointSpec.query_params.length > 0;
  const hasBody = endpointSpec.body && Object.keys(endpointSpec.body).length > 0;

  return !!(hasHeaders || hasPathParams || hasQueryParams || hasBody);
}

export function getHttpMethodColor(method: string): string {
  const colors: { [key: string]: string } = {
    GET: 'success',
    POST: 'primary',
    PUT: 'warning',
    DELETE: 'error',
    PATCH: 'info',
  };
  return colors[method] || 'default';
}
